
import type { PropOptions } from 'vue';
import Vue from 'vue';
import PinnedIcon from './images/pinned.vue.svg';
import CommentIcon from './images/preview-comment.vue.svg';
import DownvoteIcon from './images/preview-downvote.vue.svg';
import UpvoteIcon from './images/preview-upvote.vue.svg';
import type { Post, Anonymous, Public } from '@/types/graphql';
import WarRoomCategoryBadge from '@/components/partials/war-room/WarRoomCategoryBadge/WarRoomCategoryBadge.vue';
import WarRoomCategoryBadgeTopic from '@/components/partials/war-room/WarRoomCategoryBadge/WarRoomCategoryBadgeTopic.vue';
import WarRoomPostPreviewImage from '@/components/partials/war-room/WarRoomPostPreviewImage/WarRoomPostPreviewImage.vue';
import WarRoomMembersOnlyLabel from '~/components/partials/war-room/WarRoomMembersOnlyLabel/WarRoomMembersOnlyLabel.vue';
import type { IPost } from '~/types/community';
import { postTypes } from '~/components/partials/war-room/constants';
import warRoomSignUpMixin from '~/components/partials/war-room/WarRoomSignUp/warRoomSignUpMixin';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import WarRoomAvatar from '~/components/partials/war-room/WarRoomAvatar.vue';

export default Vue.extend({
  name: 'PublicPortfolioPostsListSectionWarRoomPostPresenter',
  components: {
    WarRoomAvatar,
    UpvoteIcon,
    DownvoteIcon,
    CommentIcon,
    BravadoText,
    PinnedIcon,
    WarRoomCategoryBadge,
    WarRoomCategoryBadgeTopic,
    WarRoomPostPreviewImage,
    WarRoomMembersOnlyLabel,
  },
  mixins: [warRoomSignUpMixin],
  props: {
    post: {
      type: Object,
      required: true,
    } as PropOptions<Post>,
  },
  computed: {
    titleClass(): Record<string, boolean> {
      return {
        'public-portfolio-war-room-post__title': true,
      };
    },
    link(): string {
      return `${this.$pages.POSTS_PAGE.path}/${this.post.slug}`;
    },
    isUpvoted(): boolean {
      return this.post.currentUserRating?.value === 1;
    },
    isDownvoted(): boolean {
      return this.post.currentUserRating?.value === -1;
    },
    hasPreviewImage(): boolean {
      const coercedPost: IPost = this.post as unknown as IPost;
      return Boolean(coercedPost.cover_url) || coercedPost.content.type === postTypes.LINKEDIN;
    },
    ratingClass(): Record<string, boolean> {
      return {
        'public-portfolio-war-room-post__rating': true,
        'public-portfolio-war-room-post__rating--upvoted': this.isUpvoted,
        'public-portfolio-war-room-post__rating--downvoted': this.isDownvoted,
      };
    },
    upvoteClass(): Record<string, boolean> {
      return {
        'public-portfolio-war-room-post__rate': true,
        'public-portfolio-war-room-post__rate--upvoted': this.isUpvoted,
      };
    },
    downvoteClass(): Record<string, boolean> {
      return {
        'public-portfolio-war-room-post__rate': true,
        'public-portfolio-war-room-post__rate--downvoted': this.isDownvoted,
      };
    },
    isUnreadIconVisible(): boolean {
      if (this.post.pinned) return false;
      if (!this.$auth.loggedIn) return false;
      if (this.$auth.user.slug === (this.post.author as Public).slug) return false;
      if (this.$auth.user.username === (this.post.author as Anonymous).username) return false;

      return this.post.viewed === false;
    },
    commentsButtonText(): string | number {
      if (!this.post.commentsCount) {
        return 'Comment';
      } else {
        return this.post.commentsCount;
      }
    },
    commentsButtonLink(): string {
      const linkParams = ['comment=true'];

      const link = `${this.link}?${linkParams.join('&')}`;

      return this.$auth.loggedIn ? link : `?dynamic_modal=${this.$constants.dynamicModals.SIGN_UP_MODAL}`;
    },
  },
  methods: {
    onUpvote() {
      if (!this.$auth.loggedIn) {
        // @ts-ignore, method comes from mixin
        this.openWarRoomSignUpModal();
        return;
      }

      if (this.isUpvoted) {
        this.$emit('on-remove-vote');
      } else {
        this.$emit('on-add-vote', 1);
      }
    },
    onDownvote() {
      if (!this.$auth.loggedIn) {
        // @ts-ignore, method comes from mixin
        this.openWarRoomSignUpModal();
        return;
      }

      if (this.isDownvoted) {
        this.$emit('on-remove-vote');
      } else {
        this.$emit('on-add-vote', -1);
      }
    },
  },
});
